import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../../core/services/config.service';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  AccountFeatureScopeResponse,
  Feature,
  FeaturesResponse,
  normalizeFeatureCodes,
} from '../models/feature.model';
import * as FeaturesActions from './features.actions';
import * as UserActions from '../user/user.actions';
import { selectCurrentDomainId } from '../domains/domains.selectors';
import { selectAccountUuid } from '../user/user.selectors';

@Injectable()
export class FeaturesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  private get baseUrl(): string {
    return this.configService.api.host;
  }

  loadFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeaturesActions.loadFeatures),
      withLatestFrom(this.store.select(selectCurrentDomainId)),
      switchMap(([, currentDomainId]) => {
        if (!currentDomainId) {
          return of(FeaturesActions.resetFeatures());
        }

        return this.http.get<FeaturesResponse | { data?: FeaturesResponse }>(`${this.baseUrl}/features`).pipe(
          map((response) => {
            let features: Feature[] = [];

            if (Array.isArray(response)) {
              features = response;
            } else if (Array.isArray(response?.data)) {
              features = response.data;
            }

            return FeaturesActions.loadFeaturesSuccess({ features });
          }),
          catchError((error: unknown) => {
            const errorMessage =
              error instanceof Error
                ? error.message
                : (error as { error?: { message?: string } })?.error?.message ||
                  (error as { message?: string })?.message ||
                  'Failed to load features';

            return of(FeaturesActions.loadFeaturesFailure({ error: errorMessage }));
          })
        );
      })
    )
  );

  loadAccountFeatureScopeOnUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUserSuccess),
      map(() => FeaturesActions.loadAccountFeatureScope())
    )
  );

  loadAccountFeatureScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeaturesActions.loadAccountFeatureScope),
      withLatestFrom(this.store.select(selectAccountUuid)),
      switchMap(([, accountUuid]) => {
        if (!accountUuid) {
          return of(
            FeaturesActions.loadAccountFeatureScopeFailure({
              error: 'Account UUID is required',
            })
          );
        }

        return this.http
          .get<AccountFeatureScopeResponse>(`${this.baseUrl}/features/accounts/${accountUuid}`)
          .pipe(
            map((response) =>
              FeaturesActions.loadAccountFeatureScopeSuccess({
                featureCodes: normalizeFeatureCodes(response?.data?.features),
              })
            ),
            catchError((error: unknown) => {
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : (error as { error?: { message?: string } })?.error?.message ||
                    (error as { message?: string })?.message ||
                    'Failed to load account feature scope';

              return of(
                FeaturesActions.loadAccountFeatureScopeFailure({ error: errorMessage })
              );
            })
          );
      })
    )
  );
}
